/**
 * Comparar horarios del negocio.
 *
 * Existe para una sola pregunta, la que decide si el panel avisa "tenés cambios
 * sin guardar": ¿el horario que se está editando dice algo distinto del que está
 * guardado en el servidor?
 *
 * No alcanza con `JSON.stringify(a) === JSON.stringify(b)`. La grilla de
 * horarios reescribe el día entero en cada gesto —`normalizar`, en
 * HorarioSemanal.jsx, ordena los bloques y fusiona los que se tocan—, así que el
 * orden de las claves y los campos que el objeto arrastre cambian sin que cambie
 * el horario. Comparando el texto crudo, mover un bloque y devolverlo a su lugar
 * dejaría el panel diciendo que hay cambios, y la pregunta al salir aparecería
 * sin que nadie haya cambiado nada. Un aviso que salta cuando no corresponde se
 * aprende a cerrar sin leer, y entonces deja de servir para cuando sí importa.
 *
 * Se compara sólo lo que el backend lee para calcular disponibilidad: por cada
 * día, si está abierto y, si lo está, qué rangos tiene en qué orden.
 */

const DIAS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * El día en una sola línea, o `null` si está cerrado.
 *
 * Un día cerrado resume igual tenga los rangos que tenga: cuando `enabled` es
 * falso el backend no los mira, y la grilla los conserva a propósito para poder
 * devolverlos si se lo vuelve a abrir (ver `alternarDia`). Si los rangos de un
 * día cerrado contaran, cerrar un día y volver a abrirlo daría "hay cambios".
 */
const resumirDia = (dia) => {
  if (!dia || !dia.enabled) return null;
  return (dia.ranges || [])
    .filter((r) => r && r.start && r.end)
    .map((r) => `${r.start}-${r.end}`)
    .join(',');
};

/**
 * ¿Los dos horarios dicen lo mismo?
 *
 * Un día que falta y un día cerrado son la misma cosa: las dos formas aparecen
 * en los datos reales según de dónde venga el negocio (`initializeSchedule`
 * completa los siete días, pero lo que hay guardado de antes puede tener menos).
 */
export const mismoHorario = (a, b) =>
  DIAS.every((dia) => resumirDia(a?.[dia]) === resumirDia(b?.[dia]));

export const diasDeLaSemana = DIAS;
